/**
 * Paying with Telebirr: what the apps and the functions both need to know.
 *
 * A resident starts an order through a callable, pays in Telebirr's own
 * checkout, and comes back. Nothing the phone or the browser says marks money
 * paid: the function asks Telebirr, and only an order Telebirr confirms, for the
 * amount it was opened for, becomes a verified payment. The order document is
 * how the resident's screen follows along.
 */

export const TELEBIRR_ORDER_STATUSES = ["started", "paid", "needsReview", "failed"] as const;
export type TelebirrOrderStatus = (typeof TELEBIRR_ORDER_STATUSES)[number];

/**
 * Where Telebirr sends the resident back to, with `?telebirr=<orderId>` added.
 * Fixed here, never taken from a request.
 */
export const TELEBIRR_RETURN = {
  web: "https://app.liyumanagement.com/app.html",
  phone: "liyu://pay",
} as const;
export const TELEBIRR_RETURNS = ["web", "phone"] as const;
export type TelebirrReturn = (typeof TELEBIRR_RETURNS)[number];

export interface TelebirrOrder {
  id: string;
  orgId: string;
  unitId: string;
  termId: string;
  amount: number;
  status: TelebirrOrderStatus;
  /** The verified payment it became, once it did. */
  paymentId: string | null;
  createdAt: Date | null;
}

/** An order id: letters and digits only, which every Telebirr field accepts. */
export function telebirrOrderId(orgId: string, random: string): string {
  return `${orgId.replace(/[^a-zA-Z0-9]/g, "")}${random}`.slice(0, 32).toUpperCase();
}

export function toTelebirrOrder(id: string, data: Record<string, unknown>): TelebirrOrder {
  const status = TELEBIRR_ORDER_STATUSES.find((known) => known === data.status) ?? "started";
  const created = data.createdAt as { toDate?: () => Date } | Date | undefined;
  return {
    id,
    orgId: typeof data.orgId === "string" ? data.orgId : "",
    unitId: typeof data.unitId === "string" ? data.unitId : "",
    termId: typeof data.termId === "string" ? data.termId : "",
    amount: typeof data.amount === "number" ? data.amount : 0,
    status,
    paymentId: typeof data.paymentId === "string" ? data.paymentId : null,
    createdAt:
      created instanceof Date
        ? created
        : typeof created?.toDate === "function"
          ? created.toDate()
          : null,
  };
}

/**
 * Whether a resident should see the Telebirr button: the complex has a working
 * merchant account switched on, and its plan includes it.
 */
export function offersTelebirr(organization: Record<string, unknown> | undefined): boolean {
  const capabilities = organization?.capabilities as { telebirr?: unknown } | undefined;
  const subscription = organization?.subscription as { entitlements?: unknown } | undefined;
  const entitled =
    subscription === undefined ||
    (Array.isArray(subscription.entitlements) && subscription.entitlements.includes("telebirr"));
  return capabilities?.telebirr === true && entitled;
}
